import WebMercatorViewport from 'viewport-mercator-project';
import MainMenuStore from '../../../../router/navigation/store/main-nav-container.store';
import { PanelsProperties } from './getPanelPropertiesAndDimensions';

type OffsetCoordinatesParams = {
  longitude: number,
  latitude: number,
  viewport: WebMercatorViewport,
  viewStateZoom: number | undefined,
  panelProperties: PanelsProperties,
  MAP_DEFAULT_ZOOM?: number,
  fitExactBounds?: boolean;
};

function getOffsetCoordinates({
  longitude,
  latitude,
  viewport,
  viewStateZoom,
  panelProperties: {
    isRightPanelOpen,
    isLeftPanelOpen,
    isBottomPanelOpen,
    currentLeftPanelWidth,
    currentRightPanelWidth,
    currentBottomPanelHeight,
  },
  MAP_DEFAULT_ZOOM = 12,
  fitExactBounds = false,
}: OffsetCoordinatesParams) {
  const defaultZoom = viewStateZoom || MAP_DEFAULT_ZOOM;
  let zoom = defaultZoom;

  if (viewport?.zoom) {
    zoom = Math.min(viewport.zoom, defaultZoom);
  }

  if (fitExactBounds) {
    zoom = viewport?.zoom;
  }

  // Get the ratio needed to correct the offset based on whether we use the viewport's original zoom level, or if we keep the user's zoom level as is
  const zoomCorrection = (2 ** viewport.zoom) / (2 ** zoom);

  // Plot the geographic locations on the map and convert to get the screen pixel coordinates within the viewport
  const [pixelCoordinatesX, pixelCoordinatesY] = viewport.project([longitude, latitude], { topLeft: true });

  // Half of whatever covers the map, so a fit centers on the strip of it the user
  // can see. The map is a viewport-fixed backdrop, so the nav rail counts here
  // alongside the panels — it runs over the map's left edge rather than beside it.
  const offsetY = isBottomPanelOpen && currentBottomPanelHeight ? currentBottomPanelHeight / 2 : 0;
  const offsetX = -((isLeftPanelOpen && currentLeftPanelWidth ? currentLeftPanelWidth / 2 : 0) + MainMenuStore.width / 2)
    + (isRightPanelOpen && currentRightPanelWidth ? currentRightPanelWidth / 2 : 0);

  // Get the pixel offset away from the center of the of the map based on the base offset in combination with the zoom correction
  const newPixelCoordinatesX = pixelCoordinatesX + (offsetX * zoomCorrection);
  const newPixelCoordinatesY = pixelCoordinatesY + (offsetY * zoomCorrection);

  // Convert now offset pixel coordinates back to [lng, lat, zoom] (geographic coordinates) so they can be plotted on the map
  return [...viewport.unproject([newPixelCoordinatesX, newPixelCoordinatesY]), zoom];
}

export default getOffsetCoordinates;
